<template>
  <TransactionListTemplate
    :screenConfig="setScreenConfig"
    :rowClick="rowClickCallBack"
    :setLoading="setLoading"
    :persistenceKey="'purchase-order-list'"
    v-if="loaded"
  />
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";

import TransactionListTemplate from "@/templates/transaction-list";
import { PurchaseOrderDetail } from "@/_services/api/purchasing/purchase-order-detail.service";
import { PurchaseOrder } from "@/_services/api/purchasing/purchase-order.service";
import { CommonStorage, FeatureStorage } from "@/_services/storage";
import SettingsService from "@/_services/api/settings/graycorp/settings.service";
import ColumnWidth from "@/_helpers/data-grid/ColumnWidth";

export default {
  props: {},
  mixins: [RouteMixin, FormMixin],
  components: {
    TransactionListTemplate,
  },
  data() {
    return {
      setLoading: false,
      PurchaseOrderScreenVersion: "V1",
      loaded: false,
    };
  },
  computed: {
    purchaseOrderCreateRoute() {
      if (this.PurchaseOrderScreenVersion.toString() === "V1") {
        return "/purchase-order/create";
      } else {
        return `/purchase-order-${this.PurchaseOrderScreenVersion.toLowerCase()}/create`;
      }
    },
    purchaseOrderRouteForRowClick() {
      if (this.PurchaseOrderScreenVersion.toString() === "V1") {
        return "purchase-order";
      } else {
        return `purchase-order-${this.PurchaseOrderScreenVersion.toLowerCase()}`;
      }
    },
    setScreenConfig() {
      return {
        title: "Purchase Orders",
        ScreenName: "Purchase Orders - All",
        selectedIndex: 0,
        tabs: [
          {
            text: "All",
            path: `/purchase-order`,
            applicationFeatureCode: "PurchaseOrder",
          },
          {
            text: "To Receive",
            path: `/goods-in`,
            applicationFeatureCode: "GoodsIn",
          },
          {
            text: "Received",
            path: `/goods-in-review`,
            applicationFeatureCode: "GoodsIn",
          },
          {
            text: "Invoiced",
            path: `/supplier-invoiced`,
            applicationFeatureCode: "SupplierInvoice",
          },
        ],

        gridData: {
          gridOptions: {
            dataSource: {
              endpoint: "PurchaseOrderList",
              filter: "",
              serverPaging: true,
              allowGrouping: false,
              postProcessCallback: this.poListPostProcess,
            },
            noDataMessage: {
              addNew: {
                class: "add-new",
                title: "No Purchase Orders",
                message: "Get Started, Add a PO",
              },
            },
          },

          toolbar: {
            search: false,
            columnChooser: true,
            export: false,
            openFilter: true,
            expand: false,
            customButtons: [
              {
                icon: "plus",
                label: "Add New",
                route: this.purchaseOrderCreateRoute,
                clickAction: {},
                buttonLocation: "left",
              },
            ],
          },
          columns: [
            {
              screenField: "PurchaseOrder",
              gridDataField: "Code",
              sortOrder: "desc",
              sortIndex: 0,
              allowHiding: false,
              allowSearch: false,
              width: ColumnWidth.PurchaseOrderCode.width,
              minWidth: ColumnWidth.PurchaseOrderCode.minWidth,
              forceWidth: true,
            },
            {
              screenField: "Code",
              filterLabel: "Purchase Order",
              showInColumnChooser: false,
              visible: false,
              type: "Integer",
              filterIndex: 0,
            },
            {
              dataField: "ComputedCode",
              allowSearch: true,
              visible: false,
              showInColumnChooser: false,
            },
            {
              screenField: "Supplier",
              gridDataField: "SupplierCode",
              cellTemplateRecord: {
                primary: [
                  {
                    dataField: "SupplierName",
                  },
                ],
                secondary: [
                  {
                    dataField: "SupplierCode",
                  },
                ],
              },
              exportFields: [
                {
                  dataField: "SupplierCode",
                  label: "Supplier Code",
                },
                {
                  dataField: "SupplierName",
                  label: "Supplier Name",
                },
              ],
              filterDataField: "SupplierCode",
              filterIndex: 1,
              allowHiding: false,
            },
            {
              screenField: "SupplierRef",
              dataType: "string",
              type: "Text",
              filterIndex: 2,
              width: ColumnWidth.SupplierRef.width,
              minWidth: ColumnWidth.SupplierRef.minWidth,
            },
            {
              screenField: "DateIn",
              gridLabel: "Date",
              filterLabel: "Date",
              filterIndex: 3,
            },
            {
              screenField: "LineComment",
              gridLabel: "# Lines",
              dataField: "LineInfo",
              width: ColumnWidth.LineInfo.width,
              minWidth: ColumnWidth.LineInfo.minWidth,
            },
            {
              screenField: "ETADate",
              gridLabel: "ETA Date",
              filterLabel: "ETA Date",
              filterIndex: 4,
            },
            {
              screenField: "ProductAcct",
              visible: false,
              filterIndex: 5,
              filterOperator: "contains",
              gridDataField: "ItemCodeList",
              filterDataField: "ItemCodeList",
              showInColumnChooser: false,
            },
            {
              screenField: "OrderStatus",
              gridDataField: "FriendlyStatus",
              filterDataField: "FriendlyStatus",
              filterIndex: 6,
              width: 120,
              dataSourceConfig: {
                data: [
                  "Awaiting Receipt",
                  "Completed",
                  "Cancelled",
                  "Draft",
                  "Partially Received",
                ],
              },
            },
            {
              screenField: "OrderStatus",
              gridDataField: "InvoicedFriendlyStatus",
              filterDataField: "InvoicedFriendlyStatus",
              filterIndex: 7,
              filterLabel: "Invoice Status",
              dataSourceConfig: {
                data: ["Invoiced"],
              },
            },
            {
              screenField: "GridRecordEmail",
              cssClass: "padding-7",
              ReferenceNoField: "Code",
            },
            {
              screenField: "GridRecordPDFView",
              cssClass: "padding-7",
            },
            {
              screenField: "GridRecordPrint",
              cssClass: "padding-7",
              ReferenceNoField: "Code",
            },
            {
              screenField: "GridRecordEllipsisMenu",
              cssClass: "padding-7",
              allowSearch: false,
              ellipsisMenu: {
                itemAction: (data) => {
                  return [
                    {
                      menuText: "Complete Order",
                      data: data,
                      clickAction: this.completeOrder,
                      visible: data.data.GoodsReceived === "Y",
                      disabled:
                        data.data.Status === "C" || data.data.Status === "X",
                    },
                    {
                      menuText: "Cancel Order",
                      data: data,
                      clickAction: this.cancelOrder,
                      visible: data.data.GoodsReceived === "N",
                      disabled:
                        data.data.Status === "C" || data.data.Status === "X",
                    },
                    {
                      menuText: "Invoice",
                      data: data,
                      clickAction: this.goToSupplierInvoice,
                      visible: FeatureStorage.checkPermission(
                        "SupplierInvoice",
                        "create"
                      ),
                      disabled: !(
                        data.data.GoodsReceived.toString() === "Y" &&
                        data.data.Invoiced.toString() === "N" &&
                        data.data.Status.toString() !== "X"
                      ),
                    },
                    {
                      menuText: "Copy",
                      data: data,
                      clickAction: this.copyPOFromList,
                    },
                  ];
                },
                keyExpr: "menuText",
                displayExpr: "menuText",
              },
              makeDisabled: () => {
                return !FeatureStorage.checkPermission(
                  "PurchaseOrder",
                  "create"
                );
              },
            },
          ],
        },
      };
    },
  },
  methods: {
    poListPostProcess(data) {
      data.forEach((element) => {
        element.DateChanged = element.DateChanged.dateWithOffset();
      });

      return data;
    },
    async setPurchaseOrderScreenNavigation() {
      await SettingsService.getBySettingName("PurchaseOrderVersionInUse").then(
        (res) => {
          if (res.data.value.length > 0) {
            this.PurchaseOrderScreenVersion = res.data.value[0].SettingValue;
            CommonStorage.saveItems(
              "PurchaseOrderVersionInUse",
              this.PurchaseOrderScreenVersion
            );
          }
        }
      );
    },
    copyPOFromList(e) {
      this.$router.push({
        path: `/purchase-order-v2/create/${e.Code}`,
        query: {
          copy: true,
        },
      });
    },
    goToSupplierInvoice(e) {
      this.$router.push({
        path: "/supplier-invoice/create/" + e.Code,
        query: {
          from: "purchase-order",
        },
      });
    },
    async getDetailRecord(id) {
      return await PurchaseOrderDetail.getByCode(id);
    },
    rowClickCallBack(e) {
      if (
        e.rowType.toString() === "data" &&
        e.column.screenField.toString() !== "OrderStatus" &&
        e.column.screenField.toString() !== "GridRecordEmail" &&
        e.column.screenField.toString() !== "GridRecordPDFView" &&
        e.column.screenField.toString() !== "GridRecordPrint" &&
        e.column.screenField.toString() !== "GridRecordEllipsisMenu"
      ) {
        this.onRowClicked(e, {
          view: this,
          page: this.purchaseOrderRouteForRowClick,
          mode: "review",
          idProperty: "Code",
        });
      }
    },
    async completeOrder(e, grid) {
      let confirmComplete = await this.$confirm(
        "Do you want to complete this order?",
        "Complete Order"
      );

      if (confirmComplete) {
        this.setLoading = true;
        await PurchaseOrder.completeOrder({ Code: e.Code })
          .then((res) => {
            if (res) this.setLoading = false;
            grid.refresh(true);
          })
          .catch((err) => {
            this.setLoading = false;
            this.$store.dispatch("ui/setServerError", {
              error: err,
              title: "Complete Complete Failed",
            });
          });
      }
    },
    async cancelOrder(e, grid) {
      let confirmCancel = await this.$confirm(
        "Do you want to cancel this order?",
        "Cancel Order"
      );

      if (confirmCancel) {
        this.setLoading = true;

        const payload = {
          Code: e.Code,
          DateChanged: e.DateChanged.toISOString().replace("Z", ""),
        };

        await PurchaseOrder.cancelOrder(payload)
          .then((res) => {
            if (res) this.setLoading = false;
            grid.refresh(true);
          })
          .catch((err) => {
            this.setLoading = false;
            this.$store.dispatch("ui/setServerError", {
              error: err,
              title: "Complete Cancel Failed",
            });
          });
      }
    },
  },

  async created() {
    await this.setPurchaseOrderScreenNavigation();
    this.loaded = true;
  },
};
</script>

<style lang="scss"></style>
